import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useNewMemberForm } from '../hooks/use-new-member-form';
import { useCreateNewMember } from '../hooks/use-create-new-member';
import { validateClientSide } from '../utils/client-validation';
import { NewMemberForm } from './new-member-form';

function Header({ onBack }: { onBack: () => void }): ReactNode {
  return (
    <div className="space-y-sm">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-xs text-xs text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft size={12} />
        Volver al listado
      </button>
      <h1 className="font-display text-lg font-bold text-text-primary">
        Nuevo miembro
      </h1>
    </div>
  );
}

type FormHook = ReturnType<typeof useNewMemberForm>;
type CreateHook = ReturnType<typeof useCreateNewMember>;

async function runSubmit(
  form: FormHook,
  create: CreateHook,
  onCreated: (id: string) => void
): Promise<void> {
  form.setError('');
  const clientErrs = validateClientSide(form.data);
  if (Object.keys(clientErrs).length) {
    form.setFieldErrors(clientErrs);
    return;
  }
  form.setFieldErrors({});
  const out = await create.submit({
    data: form.data,
    forcedStatus: form.forcedStatus,
    photo: form.photo,
  });
  if (out.ok) onCreated(out.id);
  else if (out.fieldErrors) form.setFieldErrors(out.fieldErrors);
}

function useNewMemberPage(): {
  form: FormHook;
  create: CreateHook;
  goBack: () => void;
  handleSubmit: () => Promise<void>;
} {
  const navigate = useNavigate();
  const form = useNewMemberForm();
  const create = useCreateNewMember();
  const goBack = (): void => {
    void navigate('/admin/team');
  };
  const onCreated = (id: string): void => {
    void navigate(`/admin/team?member=${id}`);
  };
  return {
    form,
    create,
    goBack,
    handleSubmit: () => runSubmit(form, create, onCreated),
  };
}

export function NewMemberPage(): ReactNode {
  const p = useNewMemberPage();
  return (
    <div className="mx-auto max-w-[560px] space-y-lg">
      <Header onBack={p.goBack} />
      <NewMemberForm
        {...p.form}
        pending={p.create.pending}
        onSubmit={() => void p.handleSubmit()}
        onCancel={p.goBack}
      />
    </div>
  );
}
